/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/prop-types */

/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { forwardRef } from "react";

// Core
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";

// Components
import ReactSelect, { components } from "react-select";

// Styles
import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import pxToRem from "assets/theme/functions/pxToRem";
import { animation, styles } from "components/Atoms/Select/styles";

// Custom option component with a badge
function OptionWithBadge(props) {
  return (
    <components.Option {...props}>
      {props.data.badge && (
        <span
          style={{
            backgroundColor: "#eee",
            borderRadius: "4px",
            padding: "3px 6px",
            marginRight: "6px",
          }}
        >
          {props.data.badge}
        </span>
      )}

      {props.data.label}
    </components.Option>
  );
}

const Select = forwardRef(
  (
    {
      size,
      error,
      success,
      isDisabled,
      backgroundColor,
      opensUp,
      outlineBadges, // When true, badges will have outline style instead of filled
      components: customComponents,
      ...rest
    },
    ref
  ) => {
    const classes = animation();
    const theme = useTheme();
    const isDarkMode = theme?.palette?.mode === "dark";
    const colorScheme = isDarkMode ? colors.dark : colors.light;
    const { dark, transparent, light, white } = colorScheme;
    const { borderRadius } = borders;

    // Merge custom components with default OptionWithBadge
    const mergedComponents = {
      Option: OptionWithBadge,
      ...customComponents,
    };

    // Create custom styles with optional outline badges
    let customStyles = styles(size, error, success, isDisabled, backgroundColor, theme);

    // If outlineBadges is true, override the multiValue style with outline version
    if (outlineBadges) {
      const baseStyles = styles(size, error, success, isDisabled, backgroundColor, theme);
      // In dark mode use a subtle tinted background for the chip so it reads clearly
      const chipBackground = isDarkMode ? colorScheme.grey[200] : transparent.main;
      const chipTextColor = isDarkMode ? white.main : dark.main;
      const chipHoverBackground = isDarkMode ? colorScheme.grey[300] : light.main;
      customStyles = {
        ...baseStyles,
        // Add padding to the value container for better spacing
        valueContainer: (provided) => ({
          ...baseStyles.valueContainer(provided),
          padding: `${pxToRem(4)} ${pxToRem(12)}`,
          gap: pxToRem(4),
        }),
        // Override multiValue to use outline style
        multiValue: (provided) => ({
          ...provided,
          marginRight: pxToRem(4),
          borderRadius: borderRadius.section,
          display: "flex",
          alignItems: "center",
          backgroundColor: chipBackground,
          color: chipTextColor,
          padding: `${pxToRem(2)} 0 ${pxToRem(2)} ${pxToRem(4)}`,

          "& div:first-of-type": {
            color: chipTextColor,
            paddingTop: pxToRem(4),
            paddingBottom: pxToRem(2),
          },

          "& div:last-of-type": {
            paddingTop: pxToRem(1.5),
            opacity: 0.8,
            color: chipTextColor,

            "&:hover": {
              backgroundColor: chipHoverBackground,
              color: chipTextColor,
            },
          },
        }),
      };
    }

    const reactSelectThemeOverride = (reactSelectTheme) => {
      if (!isDarkMode) {
        return {
          ...reactSelectTheme,
          colors: {
            ...reactSelectTheme.colors,
            primary25: colors.light.light.main,
            primary: colors.light.light.main,
          },
        };
      }
      const dk = colors.dark;
      return {
        ...reactSelectTheme,
        colors: {
          ...reactSelectTheme.colors,
          neutral0: dk.background.paper,
          neutral5: dk.grey[100],
          neutral10: dk.grey[200],
          neutral20: dk.grey[200],
          neutral30: dk.grey[300],
          neutral40: dk.grey[400],
          neutral50: dk.grey[500],
          neutral60: dk.grey[600],
          neutral70: dk.grey[700],
          neutral80: dk.grey[800],
          neutral90: dk.grey[900],
          primary25: dk.grey[100],
          primary50: dk.grey[200],
          primary: dk.info.main,
        },
      };
    };

    if (opensUp) {
      return (
        <ReactSelect
          isDisabled={isDisabled}
          {...rest}
          ref={ref}
          components={mergedComponents}
          styles={{
            ...customStyles,
            menu: (provided) => ({
              ...provided,
              top: "auto",
              bottom: "100%",
            }),
          }}
          className={classes.menu}
          theme={reactSelectThemeOverride}
        />
      );
    }
    return (
      <ReactSelect
        isDisabled={isDisabled}
        {...rest}
        ref={ref}
        components={mergedComponents}
        styles={customStyles}
        className={classes.menu}
        theme={reactSelectThemeOverride}
      />
    );
  }
);

// Setting default values for the props of Select
Select.defaultProps = {
  size: "medium",
  error: false,
  success: false,
  isDisabled: false,
  backgroundColor: "none",
  opensUp: false,
  outlineBadges: false, // Default to filled badges
};

// Typechecking props for the Select
Select.propTypes = {
  size: PropTypes.oneOf(["small", "medium", "large"]),
  error: PropTypes.bool,
  success: PropTypes.bool,
  isDisabled: PropTypes.bool,
  backgroundColor: PropTypes.string,
  opensUp: PropTypes.bool,
  outlineBadges: PropTypes.bool, // When true, multi-select badges use outline style
};

export default Select;
